import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sellerHome } from "@/lib/onboarding";

const DEVELOPER_PURPOSES = ["developer_credits", "developer_catalog", "developer_india"];
const WALLET = "/developers/console/wallet";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [purpose, setPurpose] = useState("cart");
  const [next, setNext] = useState("/shop");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.purpose === "seller_plan") setPurpose("seller_plan");
        if (data.purpose === "ai_credits") setPurpose("ai_credits");
        if (DEVELOPER_PURPOSES.includes(data.purpose)) setPurpose("developer");
        if (data.payment_status === "paid") {
          setStatus("paid");
          window.dispatchEvent(new Event("cart-updated"));
          if (data.purpose === "seller_plan") {
            const user = await refreshUser();
            setNext(sellerHome(user));
          }
          if (data.purpose === "ai_credits") {
            await refreshUser();
            setNext("/tryon");
          }
          if (DEVELOPER_PURPOSES.includes(data.purpose)) setNext(WALLET);
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 12) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  const seller = purpose === "seller_plan";
  const credits = purpose === "ai_credits";
  const developer = purpose === "developer";

  return (
    <div className="max-w-lg mx-auto px-6 pt-40 pb-24 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto text-aura-cyan animate-spin mb-6" />
          <h1 className="font-serif text-3xl font-bold mb-2">Confirming your payment…</h1>
          <p className="text-aura-muted">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-aura-accent mb-6 fade-up" />
          <h1 className="font-serif text-4xl font-bold mb-3">{seller ? "Plan activated" : credits || developer ? "Credits added" : "Order confirmed"}</h1>
          <p className="text-aura-muted mb-8">
            {seller
              ? "Your seller plan is active. Next, set up the shop and start listing."
              : credits
                ? "Your AI try-on credits are ready. Go back and generate another look."
                : developer
                  ? "Your API credits are in your wallet."
                  : "Thank you — your pieces are on their way. A receipt is in your orders."}
          </p>
          <div className="flex gap-3 justify-center">
            {seller ? (
              <Link to={next} className="px-7 py-3 btn-accent rounded-full text-sm font-semibold" data-testid="seller-continue-btn">Continue setup</Link>
            ) : credits ? (
              <Link to="/tryon" className="px-7 py-3 btn-accent rounded-full text-sm font-semibold" data-testid="tryon-continue-btn">Back to try-on</Link>
            ) : developer ? (
              <Link to={WALLET} className="px-7 py-3 btn-accent rounded-full text-sm font-semibold" data-testid="developer-continue-btn">Back to wallet</Link>
            ) : (
              <>
                <Link to="/orders" className="px-7 py-3 btn-accent rounded-full text-sm font-semibold" data-testid="view-orders-btn">View orders</Link>
                <Link to="/shop" className="px-7 py-3 border border-white/20 rounded-full text-sm font-semibold hover:border-aura-cyan transition-colors">Keep shopping</Link>
              </>
            )}
          </div>
        </>
      )}
      {["failed", "timeout", "error"].includes(status) && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-aura-muted mb-6" />
          <h1 className="font-serif text-3xl font-bold mb-3">We couldn't confirm payment</h1>
          <p className="text-aura-muted mb-8">
            {seller
              ? "If you were charged, refresh this page in a minute or open Subscribe again."
              : credits
                ? "If you were charged, refresh this page or open try-on again in a minute."
                : developer
                  ? "If you were charged, refresh this page or open your wallet again in a minute."
                  : "If you were charged, it'll appear in your orders shortly. Otherwise try again."}
          </p>
          <Link to={seller ? "/seller/subscribe" : credits ? "/tryon" : developer ? WALLET : "/cart"} className="px-7 py-3 btn-accent rounded-full text-sm font-semibold">
            {seller ? "Back to plans" : credits ? "Back to try-on" : developer ? "Back to wallet" : "Back to bag"}
          </Link>
        </>
      )}
    </div>
  );
}
